import { formatDistanceToNow, format as dateFnsFormat } from 'date-fns';

// Create format object with methods
export const format = {
  relativeTime: formatRelativeTime,
  duration: formatDuration,
  date: dateFnsFormat,
  bytes: formatBytes,
  number: formatNumber,
  percentage: formatPercentage,
  uptime: formatUptime,
  latency: formatLatency,
  currency: formatCurrency,
  absoluteTime: formatAbsoluteTime,
};

// Re-export format for other modules
export { dateFnsFormat };

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) {
return '0 Bytes';
}

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Format number with appropriate units (K, M, B, T)
 */
export function formatNumber(num: number, decimals = 1): string {
  if (num === 0) {
return '0';
}
  
  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['', 'K', 'M', 'B', 'T'];

  const i = Math.floor(Math.log(Math.abs(num)) / Math.log(k));

  if (i === 0) {
return num.toString();
}

  return parseFloat((num / Math.pow(k, i)).toFixed(dm)) + sizes[i];
}

/**
 * Format percentage with precision
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format duration in milliseconds to human readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
return `${ms}ms`;
}
  if (ms < 60000) {
return `${(ms / 1000).toFixed(1)}s`;
}
  if (ms < 3600000) {
return `${(ms / 60000).toFixed(1)}m`;
}
  return `${(ms / 3600000).toFixed(1)}h`;
}

/**
 * Format timestamp to relative time (e.g., "2 minutes ago")
 */
export function formatRelativeTime(date: Date | string | number): string {
  const dateObj = new Date(date);
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Format timestamp to absolute time
 */
export function formatAbsoluteTime(date: Date | string | number, formatStr = 'MMM dd, yyyy HH:mm:ss'): string {
  const dateObj = new Date(date);
  return dateFnsFormat(dateObj, formatStr);
}

/**
 * Format uptime in seconds to human readable string
 */
export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts = [];
  if (days > 0) {
parts.push(`${days}d`);
}
  if (hours > 0) {
parts.push(`${hours}h`);
}
  if (minutes > 0) {
parts.push(`${minutes}m`);
}
  if (secs > 0 || parts.length === 0) {
parts.push(`${secs}s`);
}

  return parts.join(' ');
}

/**
 * Format network latency with color coding class
 */
export function formatLatency(ms: number): { value: string; className: string } {
  let className = 'text-success-500';
  if (ms > 100) {
className = 'text-warning-500';
}
  if (ms > 500) {
className = 'text-danger-500';
}

  return {
    value: `${ms}ms`,
    className,
  };
}

/**
 * Format currency for enterprise contexts
 */
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}